import { uniqBy } from 'lodash';
import { FormSectionHandler } from '../entity.handler';
import { DistributorResponse, DistributorDetailsUpdateRequest, EmailProviderListResponse, IdNameState } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField, IdNameComboValueHandlers } from 'app/shared/components/form-builder';
import { ComboField, EMPTY_GUID, EntityUtils } from '../utils';
import { nullAccessDenied } from 'app/services';

interface EditorValues {
    emailProvider?: IdNameState;
}

export class DistributorNotificationsSectionHandler implements FormSectionHandler<DistributorResponse, EditorValues> {

    type = 'form';
    icon = 'at';
    bulk = false;
    readonly = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    constructor(
        public admin: AdminEditorService
    ) {
    }

    private properties = ['emailProvider'];

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.DISTRIBUTOR.NOTIFICATIONS.${key}`, values);
    }

    applyUpdate(entity: DistributorResponse, payload: DistributorDetailsUpdateRequest): Promise<DistributorResponse> {
        return this.admin.applyUpdate(api => api.accounts.updateDistributorDetails(entity.id, payload));
    }

    mapEntityToEditor(entity: DistributorResponse): Promise<EditorValues> {
        const result = EntityUtils.mergeFields<EditorValues>(this.properties, entity, {});
        return Promise.resolve(result);
    }

    mapEditorToEntity(entity: DistributorDetailsUpdateRequest, values: EditorValues): Promise<any> {
        const result = EntityUtils.mergeFields<EditorValues>(this.properties, entity, values);
        return Promise.resolve(result);
    }

    getFields(entity: DistributorResponse): Promise<FormBuilderField[]> {
        return Promise.all([
            this.getEmailProviders(entity),
        ]).then(([emailProviderValues]) => {
            return [
                ComboField('emailProvider', this.translate('FIELDS.EMAILPROVIDER'), true, emailProviderValues, IdNameComboValueHandlers),      
            ];
        }).then(fields => EntityUtils.prepareBulk(!entity, ['emailProvider'], fields));
    }

    async getEmailProviders(distributor: DistributorResponse): Promise<{ key: string, value: string }[]> {
        const emailProviders: EmailProviderListResponse = await this.admin.getCached(`listEmailProviders/${distributor.id}/0/100/name/state=active`, api => api.entities.listEmailProviders(distributor.id, 0, 100, 'name', 'state=active')).catch(nullAccessDenied);

        const result = [
            { id: EMPTY_GUID, name: 'Default', state: 'active' },
            ...(distributor?.availableEmailProviders || []),
            ...(emailProviders?.items || []),
        ];

        if (distributor.emailProvider && !result.find(emailProvider => emailProvider.id === distributor.emailProvider.id)) {
            result.unshift({
                id: distributor.emailProvider.id,
                name: distributor.emailProvider.name,
                state: distributor.emailProvider.state === 'active' ? 'unavailable' : distributor.emailProvider.state,
            } as any);
        }

        return uniqBy(result, 'id').map(x => {
            if (x.state && x.state !== 'active') {
                const translationKey = `STATES.${x.state.toUpperCase()}`;
                return { key: x.id, value: `${x.name} (${this.admin.translate(translationKey)})` };
            }

            return { key: x.id, value: x.name };
        }).sort((a, b) => a.value.localeCompare(b.value));
    }

}
